import { useState } from 'react';
import { Box, Button, Dialog, Typography, TextField } from '@mui/material';
import { CloseIcon } from '../../assets/icons/icons';
import SatisfactionScale from './SatisfactionScale';
import MultipleChoiceQuestion from './MultipleChoiceQuestion';

const GOAL_OPTIONS = [
  { letter: 'A', label: 'See my rate options', value: 'see_rate_options' },
  { letter: 'B', label: 'Learn more about my bill', value: 'learn_about_bill' },
  { letter: 'C', label: 'See my electrification bill impacts', value: 'electrification_impacts' },
  { letter: 'D', label: 'Other:', value: 'other', hasOtherInput: true },
];

const GOAL_ACHIEVED_OPTIONS = [
  { letter: 'A', label: 'Yes', value: 'yes' },
  { letter: 'B', label: 'No', value: 'no' },
  { letter: 'C', label: 'Other:', value: 'other', hasOtherInput: true },
];

const IMPROVEMENT_OPTIONS = [
  { letter: 'A', label: 'Connect to my PG&E account for historical energy use patterns', value: 'specific_pge_account' },
  { letter: 'B', label: 'Display comparison in a bill form instead of a chart', value: 'ixnay_chartnay' },
  { letter: 'C', label: 'Include more information about electric appliance upgrades', value: 'more_appliance_upgrades' },
  { letter: 'D', label: 'Other:', value: 'other', hasOtherInput: true },
];

export default function RatesModal({ open, onClose, onSubmit, initialRating = 0 }) {
  const [rating, setRating] = useState(initialRating);
  const [mainGoal, setMainGoal] = useState(null);
  const [mainGoalOther, setMainGoalOther] = useState('');
  const [goalAchieved, setGoalAchieved] = useState(null);
  const [goalAchievedOther, setGoalAchievedOther] = useState('');
  const [improvements, setImprovements] = useState([]);
  const [improvementsOther, setImprovementsOther] = useState('');
  const [comment, setComment] = useState('');

  const handleSubmit = () => {
    onSubmit({
      rating,
      main_goal: mainGoal,
      main_goal_other: mainGoal === 'other' ? mainGoalOther : null,
      goal_achieved: goalAchieved,
      goal_achieved_other: goalAchieved === 'other' ? goalAchievedOther : null,
      improvements,
      improvements_other: improvements.includes('other') ? improvementsOther : null,
      comment: comment || null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="rates-modal-title"
      maxWidth="md"
      sx={{ '& .MuiPaper-root': { borderRadius: '12px' } }}
    >
      <Box py={2} px={3} display="flex" flexDirection="column">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography id="rates-modal-title" fontWeight={600} fontSize="1.25rem">
            SVCE Rate Comparison Survey
          </Typography>
          <Button
            onClick={onClose}
            sx={{ minWidth: 'auto', p: 0.5 }}
            aria-label="Close survey modal"
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box sx={{ borderTop: 1, borderColor: 'grey.200', pt: 2, mb: 2 }}>
          <Typography mb={1.5}>
            What is your overall satisfaction with SVCE's Rate Comparison tool?
          </Typography>
          <SatisfactionScale value={rating} onChange={setRating} />
        </Box>

        <MultipleChoiceQuestion
          question="What is your main goal in using this tool?"
          options={GOAL_OPTIONS}
          value={mainGoal}
          onChange={setMainGoal}
          otherValue={mainGoalOther}
          onOtherChange={setMainGoalOther}
        />

        <MultipleChoiceQuestion
          question="Was your goal achieved with this tool?"
          options={GOAL_ACHIEVED_OPTIONS}
          value={goalAchieved}
          onChange={setGoalAchieved}
          otherValue={goalAchievedOther}
          onOtherChange={setGoalAchievedOther}
        />

        <MultipleChoiceQuestion
          question="How could this tool be improved?"
          options={IMPROVEMENT_OPTIONS}
          multiple
          value={improvements}
          onChange={setImprovements}
          otherValue={improvementsOther}
          onOtherChange={setImprovementsOther}
        />

        <Box sx={{ borderTop: 1, borderColor: 'grey.200', pt: 2 }}>
          <Typography mb={1}>
            Thank you for your input! Please provide any additional feedback below.
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            variant="outlined"
            size="small"
          />
        </Box>

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            onClick={handleSubmit}
            disabled={rating === 0}
            sx={{ color: 'primary.main', fontWeight: 600, textTransform: 'uppercase' }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
